import React, { useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FieldValues, useFormContext } from 'react-hook-form'
import { Button, CardRadioInput } from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from '../../InventoryCampaign/NewCampaign/CreateNewCampaignForm'
import { ReactComponent as AwarenessIcon } from '../../../images/icons/awareness_icon.svg'
import { ReactComponent as ConsiderationIcon } from '../../../images/icons/consideration_icon.svg'
import { ReactComponent as ConversionIcon } from '../../../images/icons/conversion_icon.svg'
import { ReactComponent as WandIcon } from '../../../images/icons/wand.svg'
import { toast } from 'react-hot-toast'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import { useNavigate } from 'react-router'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import InfoMessage from '../../../components/InfoMessage'

type Props = {
  step: number
  setStep: (step: number) => void
}

const CreateNonInventoryCampaignObjectives = ({ step, setStep }: Props): JSX.Element => {
  const { handleSubmit } = useFormContext()
  const navigate = useNavigate()

  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)

  const { mutateAsync: saveLinkCampaign, isLoading } = useEditLinkCampaign()

  const onSubmit = useCallback(
    async (values: Partial<FieldValues>): Promise<void> => {
      if (!values.objective) {
        toast.error('Please select an objective')
        return
      }
      setFormValues((prevFormValues) => {
        return {
          ...prevFormValues,
          objective: values?.objective,
        }
      })

      if (formValues.campaign_id) {
        await saveLinkCampaign({
          data: {
            objective: values.objective,
          },
          link_campaign_id: formValues.campaign_id,
        })
      }

      setStep(step + 1)
    },
    [step, setStep, formValues.campaign_id, saveLinkCampaign, setFormValues],
  )

  const submitOnEnter = useCallback(
    (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit(onSubmit)()
      }
    },
    [handleSubmit, onSubmit],
  )

  useEffect(() => {
    document.addEventListener('keydown', submitOnEnter)
    return () => document.removeEventListener('keydown', submitOnEnter)
  }, [submitOnEnter])

  return (
    <motion.form onSubmit={handleSubmit(onSubmit)}>
      <div className={'flex flex-col items-center overflow-auto px-12'}>
        <div className={'flex flex-row gap-5 py-20 w-full'}>
          <div className={'flex flex-1 w-1/3'}>
            <CardRadioInput
              icon={<AwarenessIcon />}
              name={'objective'}
              value={'awareness'}
              label={'Awareness'}
              description={'Get people to know your brand'}
              background={'white'}
            />
          </div>
          <div className={'flex flex-1 w-1/3'}>
            <CardRadioInput
              icon={<ConsiderationIcon />}
              name={'objective'}
              value={'consideration'}
              label={'Consideration'}
              description={'Make people think about your brand'}
              background={'white'}
            />
          </div>
          <div className={'flex flex-1 w-1/3'}>
            <CardRadioInput
              icon={<ConversionIcon />}
              name={'objective'}
              value={'conversion'}
              label={'Conversion'}
              description={'Get people wanting to see more'}
              background={'white'}
            />
          </div>
        </div>
        <div className="w-full">
          <InfoMessage
            content={
              <div className="flex flex-col">
                <span>
                  You can still <strong>make changes</strong> before going live.
                </span>
                <span>
                  Your progress is <strong>saved automatically</strong> as you progress.
                </span>
              </div>
            }
          />
        </div>
      </div>

      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full bg-white'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            navigate('..')
          }}>
          Cancel
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Continue
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNonInventoryCampaignObjectives
